import { Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardContentRibbon from 'components/foundation/dashboard/DashboardContentRibbon/DashboardContentRibbon';
import DashboardRibbonGroup from 'components/foundation/dashboard/DashboardRibbonGroup/DashboardRibbonGroup';
import {
  CertificationsExpirationOptions,
  ICertificationStatusReportFilterData,
} from 'containers/certification-status-report/CertificationStatusReportFilter/CertificationStatusReportFilter';
import { CertificationStatusReportTabType } from 'containers/certification-status-report/CertificationStatusReportTabHeader/CertificationStatusReportTabHeader';
import { useCertificationStatusReportData } from 'containers/certification-status-report/hooks/useCertificationStatusReportActiveData';
import { useActiveMember } from 'hooks';
import useCertificationsReportNavigation from 'hooks/navigation/useCertificationsReportNavigation';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { useFeatureFlags } from 'utils/features';

export interface ICertificationsDashboardCardProps {
  onHide?: () => void;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  className?: ClassName;
}

const CertificationsDashboardCard = (props: ICertificationsDashboardCardProps) => {
  const { className, theme, onHide, setting } = props;

  const member = useActiveMember();
  const isCertificationsEnabled =
    (useFeatureFlags('CERTIFICATIONS') ?? false) && (member.position?.manageCertificates ?? false);
  const navigationToReport = useCertificationsReportNavigation();
  const [loading, setLoading] = useState(false);
  const [expiredCount, setExpiredCount] = useState<string>('0');
  const [aboutToExpireCount, setAboutToExpireCount] = useState<string>('0');
  const getCertificationRecords = useCertificationStatusReportData();

  useEffect(() => {
    getCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getCounts() {
    setLoading(true);

    const expiredFilter: ICertificationStatusReportFilterData = {
      memberId: null,
      certificationId: null,
      expirationDate: null,
      reportType: CertificationStatusReportTabType.EXPIRED,
    };

    const aboutToExpireFilter: ICertificationStatusReportFilterData = {
      memberId: null,
      certificationId: null,
      expirationDate: CertificationsExpirationOptions.THIRTY_DAYS,
      reportType: CertificationStatusReportTabType.ACTIVE,
    };

    const [expired, aboutToExpire] = await Promise.all([
      getCertificationRecords(expiredFilter),
      getCertificationRecords(aboutToExpireFilter),
    ]);
    setExpiredCount(expired.length.toString());
    setAboutToExpireCount(aboutToExpire.length.toString());
    setLoading(false);
  }

  function handleNavigation(
    reportType: CertificationStatusReportTabType,
    expirationDate?: CertificationsExpirationOptions
  ) {
    navigationToReport({
      certification_expiration_date: expirationDate,
      certification_report_type: reportType,
    });
  }

  const classes = classNames('certifications-dashboard-card', className);

  return (
    <>
      {isCertificationsEnabled && (
        <DashboardRibbonGroup className={classes}>
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => handleNavigation(CertificationStatusReportTabType.EXPIRED)}
            title={t('Expired Certifications')}
            subtitle={t('All')}
            value={expiredCount}
            isLoading={loading}
          />
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() =>
              handleNavigation(CertificationStatusReportTabType.ACTIVE, CertificationsExpirationOptions.THIRTY_DAYS)
            }
            title={t('Expiring Certifications')}
            subtitle={t('Within 30 Days')}
            value={aboutToExpireCount}
            isLoading={loading}
          />
        </DashboardRibbonGroup>
      )}
    </>
  );
};

export default CertificationsDashboardCard;
